/**
 * A social post shown the way followers will see it: the page name, the caption with its links, and the picture.
 * Used by the review screen for Facebook / Instagram / LinkedIn / X items instead of the article reader.
 */
import { Fragment } from "react";
import { ImageOff } from "lucide-react";
import { platformKey, type StudioItem } from "./helpers";

const URL_RE = /(https?:\/\/[^\s<>"')]+)/g;

function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <a key={i} href={p} target="_blank" rel="noreferrer" className="break-all text-blue-400 hover:underline">
            {p.replace(/\?utm_[^\s]*$/, "")}
          </a>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

const CHANNEL_NAME: Record<string, string> = { facebook: "Facebook", instagram: "Instagram", linkedin: "LinkedIn", x: "X", twitter: "X" };

export function socialCaptionOf(item: StudioItem): string {
  const doc = String(item.documentContent || "").trim();
  const cap = String(item.caption || "").trim();
  // Worker spokes keep the post text in caption; older packages put it in documentContent
  return (cap.length >= doc.length ? cap : doc).replace(/<[^>]+>/g, "");
}

export function SocialPreview({ item, workspace }: { item: StudioItem; workspace: any }) {
  const p = platformKey(item);
  const sc: any = (item as any).sourceContext || {};
  const alsoInstagram = p === "facebook" && workspace?.publishPolicy?.facebook?.alsoInstagram !== false && !!item.mediaUrl;
  const name = workspace?.brandName || workspace?.name || "Your page";
  const alt = sc.image?.alt || sc.diagram?.alt || "";
  const caption = socialCaptionOf(item);

  return (
    <section className="space-y-2" data-testid="social-preview">
      <p className="text-xs text-muted-foreground">
        How it will look on {CHANNEL_NAME[p] ?? p}
        {alsoInstagram ? " (and Instagram, with the same picture)" : ""}
      </p>
      <article className="mx-auto max-w-[500px] overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <header className="flex items-center gap-3 px-4 pt-4">
          {workspace?.logoUrl ? (
            <img src={workspace.logoUrl} alt="" className="h-10 w-10 rounded-full border border-border bg-white object-contain" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
              {String(name).slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{name}</p>
            <p className="text-xs text-muted-foreground">Draft · not posted yet</p>
          </div>
        </header>
        <div className="whitespace-pre-wrap break-words px-4 py-3 text-[15px] leading-relaxed text-foreground" data-testid="social-caption">
          <Linkified text={caption} />
        </div>
        {item.mediaUrl ? (
          <img src={item.mediaUrl} alt={alt} className="w-full border-t border-border bg-muted object-cover" data-testid="social-image" />
        ) : (
          <div className="flex items-center gap-2 border-t border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground" data-testid="social-no-image">
            <ImageOff size={16} /> No picture on this post{p === "facebook" ? ", so it will not go to Instagram" : ""}.
          </div>
        )}
      </article>
    </section>
  );
}
